import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setErrorMessages } from '../app';
import {
  fetchOutboundFlights,
  fetchInboundFlights,
  getFilghtInputValidationErrors,
} from '../flight';
import { makeFlightsInput } from '../../utils/input';

const selector = (state) => ({
  flight: state.flight,
  hotel: state.hotel,
  plan: state.plan,
});

// フライト検索を順列に行う https://gitlab.fqsv.net/dp-plus/front/-/issues/358
export const useSearchFlightSync = (
  initSearch = true, disableRefreshOnInputChange = false, useSearchedInput = false, useCache = false,
) => {
  const dispatch = useDispatch();
  const { flight, hotel, plan } = useSelector(selector);
  const {
    flightsInput,
    searchedFlightsInput,
    queryParamsValidated: queryParamsValidatedFlight,
  } = flight;
  const {
    queryParamsValidated: queryParamsValidatedHotel,
  } = hotel;

  const [fetchedOutbound, setFetchedOutbound] = useState(!initSearch);
  const [fetchedInbound, setFetchedInbound] = useState(!initSearch);
  const [hasError, setHasError] = useState(false);

  const params = useSearchedInput ? searchedFlightsInput : flightsInput;
  const cacheFlag = useCache;

  useEffect(
    () => {
      if (!queryParamsValidatedFlight || !queryParamsValidatedHotel) {
        return;
      }

      if (!params) {
        return;
      }

      if (hasError) {
        return;
      }

      const { people, airline, outbound, inbound } = params;
      const outboundFlightsInput = makeFlightsInput(people, airline, outbound, cacheFlag);
      const errorMessages = getFilghtInputValidationErrors(outboundFlightsInput);
      if (errorMessages && errorMessages.length) {
        setHasError(true);
        return;
      }

      const departureDate = outbound.date;

      if (!fetchedOutbound) {
        dispatch(
          fetchOutboundFlights(
            makeFlightsInput(people, airline, outbound, departureDate, cacheFlag),
            1,
            1,
            disableRefreshOnInputChange,
            () => setFetchedOutbound(true),
          )
        );
      }

      if (fetchedOutbound && !fetchedInbound) {
        dispatch(
          fetchInboundFlights(
            makeFlightsInput(people, airline, inbound, departureDate, cacheFlag),
            1,
            1,
            disableRefreshOnInputChange,
            () => setFetchedInbound(true),
          )
        );
      }
    },
    [
      queryParamsValidatedFlight, queryParamsValidatedHotel,
      params, fetchedOutbound, fetchedInbound, hasError, setHasError,
    ]
  );

  const handleFlightSearch = useCallback(() => {
    if (!params) {
      return false;
    }

    const { people, airline, outbound } = params;
    const departureDate = outbound.date;
    const outboundFlightsInput = makeFlightsInput(people, airline, outbound, departureDate, cacheFlag);
    const errorMessages = getFilghtInputValidationErrors(outboundFlightsInput);
    if (errorMessages && errorMessages.length) {
      dispatch(setErrorMessages(errorMessages));
      setHasError(true);
      return false;
    }

    setHasError(false);
    setFetchedOutbound(false);
    setFetchedInbound(false);
    return true;
  },[params, setHasError]);

  return { dispatch, flight, hotel, plan, handleFlightSearch };
};
